import { useState, useCallback, useEffect, useRef, memo } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Fade
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { getSentence } from './translations';

// Constants moved outside component to prevent recreation on each render
const SEARCH_FIELDS = [
  { value: 'title', label: 'Title' },
  { value: 'summary', label: 'Summary' },
  { value: 'keypoints', label: 'Key Points' },
  { value: 'speaker', label: 'Participants' },
  { value: 'transcript', label: 'Transcript' }
];

// Default values to prevent recreation
const DEFAULT_SELECTED_FIELDS = ['title']; // Default to title only

const MeetingSearchBar = memo(({
  onSearch,
  onClear,
  isSearching = false,
  searchResults = null,
  // Sorting props passed from parent
  orderBy = 'id',
  descending = true
}) => {
  const [keywords, setKeywords] = useState('');
  const [selectedFields, setSelectedFields] = useState(DEFAULT_SELECTED_FIELDS);
  const lastSearchParamsRef = useRef('');

  // Memoized handlers to prevent unnecessary re-renders
  const handleFieldChange = useCallback((event, newFields) => {
    if (newFields.length > 0) { // Ensure at least one field is selected
      setSelectedFields(newFields);
    }
  }, []);

  const handleKeywordsChange = useCallback((event) => {
    setKeywords(event.target.value);
  }, []);



  // Auto-search on every keystroke with debounce
  useEffect(() => {
    const currentSearchParams = `${keywords.trim()}-${selectedFields.join(';')}`;
    
    // Only search if search content actually changed (not sorting)
    if (currentSearchParams === lastSearchParamsRef.current) {
      return;
    }
    
    // Clear previous results immediately when search parameters change
    if (keywords.trim() && lastSearchParamsRef.current !== '') {
      onClear?.(); // This will clear searchResults immediately
    }
    
    const timeoutId = setTimeout(() => {
      lastSearchParamsRef.current = currentSearchParams;
      
      if (!keywords.trim()) {
        onClear?.();
        return;
      }
      
      const searchParams = {
        keywords: keywords.trim(),
        fields: selectedFields.join(';'),
        orderBy,
        desc: descending ? 1 : 0
      };
      
      onSearch?.(searchParams);
    }, 800); // 800ms debounce - increased for better performance

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywords, selectedFields, onSearch, onClear]); // Intentionally exclude orderBy and descending to prevent re-search on sort changes

  // Handle clear search
  const handleClear = useCallback(() => {
    setKeywords('');
    setSelectedFields(DEFAULT_SELECTED_FIELDS); // Reset to default fields
    onClear?.();
  }, [onClear]);







  return (
    <Box sx={{ 
        padding: '12px 16px',
        backgroundColor: 'white',
        borderBottom: '1px solid #e9ecef',
      }}>
        <Stack spacing={2}>
        {/* Search Input */}
        <Box sx={{ position: 'relative' }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={getSentence(158)}
            value={keywords}
            onChange={handleKeywordsChange}
            disabled={false} // Never disable to maintain focus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ 
                    color: isSearching ? 'var(--mainColor)' : '#adb5bd',
                    transition: 'color 0.3s ease',
                    fontSize: 20
                  }} />
                </InputAdornment>
              ),
              endAdornment: searchResults !== null && (
                <InputAdornment position="end">
                  <Fade in={true}>
                    <IconButton
                      onClick={handleClear}
                      size="small"
                      sx={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        width: 28,
                        height: 28,
                        '&:hover': {
                          backgroundColor: '#c82333',
                          transform: 'scale(1.1)',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Fade>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
                borderRadius: 2,
                height: 40,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '& fieldset': {
                  borderColor: '#dee2e6',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: 'var(--mainColor)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--mainColor)',
                  boxShadow: '0 0 0 2px rgba(33, 150, 243, 0.1)',
                },
                '& .MuiInputBase-input': {
                  padding: '8px 0',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  color: '#495057',
                  '&::placeholder': {
                    color: '#adb5bd',
                    opacity: 1,
                    fontWeight: 400,
                  },
                },
              },
            }}
          />
        </Box>

        {/* Search Fields Selection - Only visible when actively searching */}
        <Fade in={!!keywords.trim()} timeout={300}>
          <Box sx={{ 
            display: keywords.trim() ? 'block' : 'none',
          }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#6c757d', 
                marginBottom: 1,
                fontWeight: 600,
                fontSize: '0.8rem',
                letterSpacing: '0.025em'
              }}
            >
              Search in:
            </Typography>
            <Box 
              sx={{ 
                display: 'flex',
                overflowX: 'auto',
                gap: 1,
                paddingBottom: 0.5,
                '&::-webkit-scrollbar': {
                  height: 4,
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: '#f8f9fa',
                  borderRadius: 2,
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#dee2e6',
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: '#adb5bd',
                  }
                }
              }}
            >
              <ToggleButtonGroup
                value={selectedFields}
                onChange={handleFieldChange}
                sx={{
                  display: 'flex',
                  flexShrink: 0,
                  gap: 0.5,
                  '& .MuiToggleButtonGroup-grouped': {
                    margin: 0,
                    border: '1px solid #dee2e6',
                    borderRadius: '6px !important',
                    whiteSpace: 'nowrap',
                    minWidth: 'auto',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    textTransform: 'none',
                    padding: '4px 12px',
                    height: 28,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&.Mui-selected': {
                      backgroundColor: 'var(--mainColor)',
                      color: 'white',
                      borderColor: 'var(--mainColor)',
                      fontWeight: 600,
                      boxShadow: '0 2px 4px rgba(33, 150, 243, 0.2)',
                      '&:hover': {
                        backgroundColor: '#1976d2',
                        borderColor: '#1976d2',
                        boxShadow: '0 3px 6px rgba(33, 150, 243, 0.3)',
                      }
                    },
                    '&:not(.Mui-selected)': {
                      color: '#6c757d',
                      borderColor: '#dee2e6',
                      backgroundColor: 'white',
                      '&:hover': {
                        backgroundColor: '#f8f9fa',
                        borderColor: '#adb5bd',
                        color: '#495057',
                      }
                    }
                  }
                }}
              >
                {SEARCH_FIELDS.map((field) => (
                  <ToggleButton 
                    key={field.value} 
                    value={field.value}
                  >
                    {field.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
        </Fade>

        {/* Search Results Info - Only show count when results found and not searching */}
        <Fade in={searchResults !== null && searchResults.length > 0 && !isSearching} timeout={400}>
          <Box sx={{ display: searchResults !== null && searchResults.length > 0 && !isSearching ? 'block' : 'none' }}>
            <Box sx={{
              backgroundColor: '#e8f5e8',
              border: '1px solid #c8e6c9',
              borderRadius: 1.5,
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}>
              <SearchIcon sx={{ color: '#2e7d32', fontSize: 16 }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#2e7d32',
                  fontWeight: 600,
                  fontSize: '0.8rem'
                }}
              >
                Found {searchResults?.length || 0} meeting{(searchResults?.length || 0) !== 1 ? 's' : ''} matching "{keywords}"
              </Typography>
            </Box>
          </Box>
        </Fade>
        </Stack>
      </Box>
  );
});

MeetingSearchBar.displayName = 'MeetingSearchBar';

export default MeetingSearchBar;